import { Controller } from '@hotwired/stimulus'
import SignaturePad from 'signature_pad'

export default class extends Controller {
  static targets = [
    'canvas',
    'input',
    'timestamp',
    'display',
    'label',
    'placeholder'
  ]

  static values = {
    penColor: {
      type: String,
      default: 'rgb(0, 0, 0)'
    },
    backgroundColor: {
      type: String,
      default: 'rgb(255, 255, 255)'
    }
  }

  declare readonly canvasTarget: HTMLCanvasElement
  declare readonly inputTarget: HTMLInputElement
  declare readonly timestampTarget: HTMLInputElement
  declare readonly displayTarget: HTMLElement
  declare readonly labelTarget: HTMLElement
  declare readonly placeholderTarget: HTMLElement

  declare readonly penColorValue: string
  declare readonly backgroundColorValue: string

  private pad!: SignaturePad
  private resizeObserver!: ResizeObserver
  private formElement: HTMLFormElement | null = null

  private readonly resizeCanvas = (): void => {
    const canvas = this.canvasTarget
    const ratio  = Math.max(window.devicePixelRatio || 1, 1)
    const rect   = canvas.getBoundingClientRect()

    canvas.width  = rect.width * ratio
    canvas.height = rect.height * ratio

    const ctx = canvas.getContext('2d')

    if (ctx) ctx.scale(ratio, ratio)

    if (this.pad) this.pad.clear()
  }

  private readonly onFirstStroke = (): void => {
    this.placeholderTarget.style.display = 'none'

    const now = new Date()

    this.timestampTarget.value = now.toISOString()
    this.labelTarget.textContent = this.formatDate(now)
    this.labelTarget.classList.add('timestamp-set')
  }

  private readonly syncInput = (): void => {
    if (this.pad.isEmpty()) return

    this.inputTarget.value = this.pad.toDataURL('image/png')

    const now = new Date()

    this.timestampTarget.value   = now.toISOString()
    this.labelTarget.textContent = this.formatDate(now)
    this.labelTarget.classList.add('timestamp-set')
  }

  private readonly formSubmit = (_event: Event): void => {
    this.syncInput()
  }

  connect(): void {
    this.pad = new SignaturePad(this.canvasTarget, {
      backgroundColor: this.backgroundColorValue,
      penColor: this.penColorValue
    })

    this.resizeCanvas()

    this.resizeObserver = new ResizeObserver(this.resizeCanvas)
    this.resizeObserver.observe(this.canvasTarget)

    this.pad.addEventListener('beginStroke', this.onFirstStroke)
    this.pad.addEventListener('endStroke', this.syncInput)

    this.formElement = this.element.closest<HTMLFormElement>('form')

    if (this.formElement) this.formElement.addEventListener('submit', this.formSubmit)
  }

  disconnect(): void {
    this.resizeObserver?.disconnect()
    this.pad?.off()

    if (this.formElement) {
      this.formElement.removeEventListener('submit', this.formSubmit)
    }
  }

  clear(): void {
    this.pad.clear()
    this.inputTarget.value = ''
    this.timestampTarget.value = ''
    this.placeholderTarget.style.display = ''
    this.labelTarget.textContent = 'Updated automatically'
    this.labelTarget.classList.remove('timestamp-set')
  }

  private formatDate(date: Date): string {
    const datePart = new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }).format(date)

    const timePart = new Intl.DateTimeFormat('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    }).format(date)

    return `${datePart} | ${timePart}`
  }
}
